import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Drawer, Input, Button, Space, Typography, Badge, Tooltip, Empty } from 'antd';
import {
  MessageOutlined,
  SendOutlined,
  CloseOutlined,
  DeleteOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import ChatMessage, { ChatMessageData, ToolCallInfo } from './ChatMessage';

const { Text } = Typography;
const { TextArea } = Input;

const CHAT_WIDTH = 420;

const SUGGESTIONS = [
  'Any pending MRs or POs?',
  'Create an MR draft for my department',
  'Create a draft like MR-EMB-000068',
  'What did we buy for Riyadh this month?',
];

const ChatDrawer: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [agentAvailable, setAgentAvailable] = useState<boolean | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);

  // Check agent availability on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch((process.env.REACT_APP_API_URL || '/api') + '/agent/status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setAgentAvailable(data.available))
      .catch(() => setAgentAvailable(false));
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  // Keyboard shortcut: Ctrl+K to toggle
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || loading) return;

    const userMsg: ChatMessageData = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    };

    const assistantMsgId = `msg_${Date.now()}_assistant`;
    const assistantMsg: ChatMessageData = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      toolCalls: [],
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch((process.env.REACT_APP_API_URL || '/api') + '/agent/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: text.trim(), conversationId }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Request failed' }));
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMsgId ? { ...m, content: `Error: ${err.error || 'Something went wrong'}` } : m,
          ),
        );
        setLoading(false);
        return;
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            try {
              const data = JSON.parse(dataStr);

              if (eventType === 'text') {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsgId
                      ? { ...m, content: m.content + data.content }
                      : m,
                  ),
                );
              } else if (eventType === 'tool_start') {
                const newTool: ToolCallInfo = { name: data.name, status: 'running' };
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsgId
                      ? { ...m, toolCalls: [...(m.toolCalls || []), newTool] }
                      : m,
                  ),
                );
              } else if (eventType === 'tool_end') {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsgId
                      ? {
                          ...m,
                          toolCalls: (m.toolCalls || []).map(t =>
                            t.name === data.name && t.status === 'running'
                              ? { ...t, status: data.status === 'error' ? 'error' as const : 'done' as const, count: data.count }
                              : t,
                          ),
                        }
                      : m,
                  ),
                );
              } else if (eventType === 'done') {
                if (data.conversationId) setConversationId(data.conversationId);
              } else if (eventType === 'error') {
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantMsgId
                      ? { ...m, content: m.content || `Error: ${data.message}` }
                      : m,
                  ),
                );
              }
            } catch {
              // Skip malformed data lines
            }
            eventType = '';
          }
        }
      }
    } catch (err: any) {
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMsgId
            ? { ...m, content: `Connection error: ${err.message || 'Failed to reach the server'}` }
            : m,
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [loading, conversationId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConversationId(undefined);
  };

  // Don't render if agent isn't available
  if (agentAvailable === false) return null;

  return (
    <>
      {/* Floating Chat Button */}
      {!open && (
        <Tooltip title="ERP Assistant (Ctrl+K)" placement="left">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={
              <Badge count={0} size="small">
                <MessageOutlined style={{ fontSize: 22, color: '#fff' }} />
              </Badge>
            }
            onClick={() => setOpen(true)}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              width: 56,
              height: 56,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              background: '#722ed1',
              borderColor: '#722ed1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Tooltip>
      )}

      {/* Chat Drawer */}
      <Drawer
        title={
          <Space>
            <RobotOutlined style={{ color: '#722ed1' }} />
            <Text strong>ERP Assistant</Text>
          </Space>
        }
        placement="right"
        width={CHAT_WIDTH}
        open={open}
        onClose={() => setOpen(false)}
        mask={false}
        styles={{
          body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' },
          header: { borderBottom: '1px solid #f0f0f0' },
        }}
        extra={
          <Space>
            <Tooltip title="Clear conversation">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={clearChat}
                disabled={messages.length === 0}
              />
            </Tooltip>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
            />
          </Space>
        }
      >
        {/* Messages Area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 12px',
            background: '#fafafa',
          }}
        >
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', paddingTop: 40 }}>
              <RobotOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Ask me anything about your ERP data
              </Text>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {SUGGESTIONS.map((s, i) => (
                  <Button
                    key={i}
                    type="dashed"
                    size="small"
                    block
                    onClick={() => sendMessage(s)}
                    style={{ textAlign: 'left', whiteSpace: 'normal', height: 'auto', padding: '6px 12px' }}
                  >
                    {s}
                  </Button>
                ))}
              </Space>
            </div>
          ) : (
            messages.map(msg => <ChatMessage key={msg.id} message={msg} />)
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div
          style={{
            padding: '12px',
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
          }}
        >
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about deals, POs, clients..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={loading}
              style={{ borderRadius: '8px 0 0 8px' }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => sendMessage(input)}
              loading={loading}
              disabled={!input.trim()}
              style={{
                height: 'auto',
                borderRadius: '0 8px 8px 0',
                background: '#722ed1',
                borderColor: '#722ed1',
              }}
            />
          </Space.Compact>
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            Shift+Enter for new line | Ctrl+K to toggle
          </Text>
        </div>
      </Drawer>
    </>
  );
};

export default ChatDrawer;
